import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { apiFetch } from '../lib/api';
import type { User } from '@nasabtree/shared-types';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth, accessToken } = useAuthStore();

  const redirectTo = searchParams.get('redirect') || '/trees';

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Handle Google OAuth callback — tokens arrive in the URL hash fragment
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const errParam = params.get('error');
    const token = params.get('access_token');

    window.history.replaceState(null, '', window.location.pathname);

    if (errParam) {
      setError(`Google sign-in failed: ${errParam}`);
      return;
    }

    if (token) {
      apiFetch<{ user: User }>('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      }).then((data) => {
        if (data.ok) {
          setAuth(data.data.user, token);
          navigate(redirectTo, { replace: true });
        } else {
          setError(t('common.error'));
        }
      }).catch(() => setError(t('common.error')));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already authenticated — redirect
  useEffect(() => {
    if (accessToken) navigate(redirectTo, { replace: true });
  }, [accessToken, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch<{ message?: string; user?: User; access_token?: string }>('/api/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        if (res.data.access_token && res.data.user) {
          // Dev bypass — direct login
          setAuth(res.data.user, res.data.access_token);
          navigate(redirectTo, { replace: true });
        } else {
          setSent(true);
        }
      } else {
        setError(res.error || t('common.error'));
      }
    } catch {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    try {
      const res = await apiFetch<{ url: string }>('/api/auth/google');
      if (res.ok && res.data?.url) window.location.href = res.data.url;
      else setError(t('common.error'));
    } catch {
      setError(t('common.error'));
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-700 text-white flex-col justify-center items-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 400 400" className="w-full h-full" aria-hidden="true">
            <circle cx="200" cy="200" r="180" fill="none" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="200" cy="200" r="130" fill="none" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="200" cy="200" r="80" fill="none" stroke="currentColor" strokeWidth="0.5" />
            <line x1="200" y1="20" x2="200" y2="380" stroke="currentColor" strokeWidth="0.5" />
            <line x1="20" y1="200" x2="380" y2="200" stroke="currentColor" strokeWidth="0.5" />
          </svg>
        </div>
        <div className="relative z-10 text-center max-w-sm">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v18M12 3c-2 4-6 6-9 7m9-7c2 4 6 6 9 7M12 10c-1.5 2.5-4 4-6 5m6-5c1.5 2.5 4 4 6 5" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-3">{t('app.name')}</h1>
          <p className="text-brand-200 text-lg leading-relaxed">
            {t('auth.tagline')}
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-12 h-12 bg-brand-700 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v18M12 3c-2 4-6 6-9 7m9-7c2 4 6 6 9 7M12 10c-1.5 2.5-4 4-6 5m6-5c1.5 2.5 4 4 6 5" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">{t('app.name')}</h1>
          </div>

          {sent ? (
            /* ── Magic link sent confirmation ──────────────────── */
            <div className="text-center">
              <div className="w-14 h-14 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-brand-600" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {t('auth.checkEmail')}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {t('auth.magicLinkSent', { email })}
              </p>
              <button
                onClick={() => { setSent(false); setEmail(''); }}
                className="text-sm text-brand-600 hover:text-brand-700 font-semibold hover:underline"
              >
                {t('auth.tryDifferentEmail')}
              </button>
            </div>
          ) : (
            /* ── Email input form ─────────────────────────────── */
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {t('auth.login')}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {t('auth.magicLinkSubtitle')}
              </p>

              {error && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('auth.email')}
                  </label>
                  <input
                    type="email"
                    required
                    dir="ltr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {loading ? t('common.loading') : t('auth.sendMagicLink')}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">{t('auth.or')}</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <button
                onClick={() => void handleGoogle()}
                className="w-full border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2.5 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.loginWithGoogle')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
